import { seatTypes } from '../utils/seatUtils';

/**
 * Rich right-click menu — primary place for seat/zone actions (ticket-map UX).
 */
export default function ContextMenu({
  contextMenu,
  selectedSeats = [],
  seats = [],
  selectedSeatType = 'REGULAR',
  setSelectedSeatType,
  enablePositioningMode,
  enableSingleSeatPrecisionMode,
  disableDetachedMode,
  clearSelectedSeats,
  addRowFromPosition,
  fillEmptyAreaWithSeats,
  setEmptyAreaSelection,
  setContextMenu,
  deleteZone,
  enterZone,
  addSeatAt,
  fillZoneWithSeats,
  fillZoneTribune,
  setAddSeatMode,
  setActiveTool,
  changeSelectedSeatType,
  renameSeats,
  clearCustomSeatLabels,
  duplicateSelectedSeats,
  emptyColumn,
  voidColumn,
  voidRow,
  emptySelectedArea,
  emptySelectedAsAisle,
  emptySeatAt,
  emptySeatAtAsAisle,
  restoreColumn,
  naming,
  copyZone,
  pasteZone,
  duplicateZone,
}) {
  if (!contextMenu || !contextMenu.visible) return null;

  const close = () => setContextMenu(null);

  const MenuItem = ({ onClick, color = 'text-gray-700', children, danger }) => (
    <button
      type="button"
      onClick={() => {
        onClick && onClick();
        close();
      }}
      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
        danger ? 'text-red-600 hover:bg-red-50' : color
      }`}
    >
      {children}
    </button>
  );

  const Divider = () => <div className="h-px bg-gray-200 my-1" />;
  const Label = ({ children }) => (
    <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
      {children}
    </div>
  );

  const selectedSeatObjects = seats.filter((seat) => {
    const seatKey = seat.isDetached
      ? `${seat.row}-${seat.col}`
      : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
    return selectedSeats.includes(seatKey);
  });
  const hasDetached = selectedSeatObjects.some((s) => s.isDetached);
  const pos = contextMenu.gridPosition;
  const zone = contextMenu.zone;
  const colIsAisle = pos && naming?.aisleCols?.includes(pos.col);
  const colIsVoid = pos && naming?.voidCols?.includes(pos.col);
  const rowIsVoid = pos && naming?.voidRows?.includes(pos.row);

  const ColumnRowActions = () =>
    pos ? (
      <>
        <Divider />
        <Label>Kolon / satır</Label>
        <MenuItem onClick={() => emptyColumn && emptyColumn(pos.col)} color="text-amber-700">
          Bu kolonu boş bırak (koridor) — numara devam eder
        </MenuItem>
        {colIsAisle || colIsVoid ? (
          <MenuItem onClick={() => restoreColumn && restoreColumn(pos.col)} color="text-green-700">
            Bu kolonu geri aç
          </MenuItem>
        ) : (
          <MenuItem onClick={() => voidColumn && voidColumn(pos.col)} color="text-orange-700">
            Bu kolonu geçersiz kıl
          </MenuItem>
        )}
        <MenuItem onClick={() => voidRow && voidRow(pos.row)} color="text-orange-700">
          {rowIsVoid ? 'Bu satırı geri aç / geçersiz' : 'Bu satırı geçersiz kıl'}
        </MenuItem>
      </>
    ) : null;

  return (
    <div
      className="context-menu fixed bg-white border border-gray-200 rounded-xl shadow-2xl py-1.5 z-[10050] min-w-[240px] max-w-[300px]"
      style={{ left: contextMenu.x, top: contextMenu.y }}
    >
      {/* —— Selected seats —— */}
      {(contextMenu.type === 'selectedSeats' || contextMenu.type === 'zone-edit-selectedSeats') && (
        <>
          <Label>Seçili ({selectedSeats.length})</Label>
          <MenuItem
            onClick={() => emptySelectedAsAisle && emptySelectedAsAisle()}
            color="text-amber-700"
          >
            Seçili alanı boş bırak (koridor) — numara devam eder
          </MenuItem>
          <MenuItem onClick={() => emptySelectedArea && emptySelectedArea()} danger>
            Seçili alanı boş kıl (koltukları sil)
          </MenuItem>
          {hasDetached ? (
            <MenuItem onClick={disableDetachedMode} color="text-green-700">
              Yersizleştirmeyi kapat
            </MenuItem>
          ) : selectedSeats.length === 1 ? (
            <MenuItem onClick={enableSingleSeatPrecisionMode} color="text-orange-600">
              Hassas taşı (yersizleştir)
            </MenuItem>
          ) : (
            <MenuItem onClick={enablePositioningMode} color="text-orange-600">
              Grubu taşı / yerleştir
            </MenuItem>
          )}
          {duplicateSelectedSeats && (
            <>
              <MenuItem onClick={() => duplicateSelectedSeats('right')} color="text-blue-700">
                Çoğalt sağa (B1 → B2 → B3)
              </MenuItem>
              <MenuItem onClick={() => duplicateSelectedSeats('down')} color="text-blue-700">
                Çoğalt aşağı (B1 → C1 → D1)
              </MenuItem>
            </>
          )}
          {renameSeats && (
            <MenuItem onClick={() => renameSeats()} color="text-indigo-700">
              İsim ver / değiştir
            </MenuItem>
          )}
          {clearCustomSeatLabels && (
            <MenuItem onClick={() => clearCustomSeatLabels()} color="text-gray-600">
              Otomatik isme dön
            </MenuItem>
          )}
          <Divider />
          <Label>Tip değiştir</Label>
          {seatTypes.map((t) => (
            <MenuItem
              key={t.value}
              onClick={() => changeSelectedSeatType && changeSelectedSeatType(t.value)}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
              {t.label}
            </MenuItem>
          ))}
          <ColumnRowActions />
        </>
      )}

      {/* —— Single seat —— */}
      {(contextMenu.type === 'seat' || contextMenu.type === 'zone-edit-seat') && (
        <>
          <Label>
            Koltuk {contextMenu.seat?.label || `${pos?.row}-${pos?.col}`}
            {contextMenu.seat?.customLabel ? ' (elle)' : ''}
          </Label>
          {renameSeats && pos && (
            <MenuItem
              onClick={() => renameSeats([`${Math.round(pos.row)}-${Math.round(pos.col)}`])}
              color="text-indigo-700"
            >
              İsim ver / değiştir
            </MenuItem>
          )}
          {clearCustomSeatLabels && pos && contextMenu.seat?.customLabel && (
            <MenuItem
              onClick={() =>
                clearCustomSeatLabels([`${Math.round(pos.row)}-${Math.round(pos.col)}`])
              }
              color="text-gray-600"
            >
              Otomatik isme dön
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              if (!pos) return;
              if (emptySeatAtAsAisle) emptySeatAtAsAisle(pos.row, pos.col);
              else if (emptySeatAt) emptySeatAt(pos.row, pos.col);
            }}
            color="text-amber-700"
          >
            Bu koltuğu boş bırak (koridor) — numara devam eder
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!pos) return;
              if (emptySeatAt) emptySeatAt(pos.row, pos.col);
              else {
                const key = `${pos.row}-${pos.col}`;
                clearSelectedSeats && clearSelectedSeats([key]);
              }
            }}
            danger
          >
            Bu koltuğu boş kıl (sil)
          </MenuItem>
          {duplicateSelectedSeats && pos && (
            <>
              <MenuItem
                onClick={() =>
                  duplicateSelectedSeats('right', [`${Math.round(pos.row)}-${Math.round(pos.col)}`])
                }
                color="text-blue-700"
              >
                Çoğalt sağa (→ B2)
              </MenuItem>
              <MenuItem
                onClick={() =>
                  duplicateSelectedSeats('down', [`${Math.round(pos.row)}-${Math.round(pos.col)}`])
                }
                color="text-blue-700"
              >
                Çoğalt aşağı (→ C1)
              </MenuItem>
            </>
          )}
          {selectedSeats.length > 0 && (
            <>
              <Divider />
              <MenuItem
                onClick={() => emptySelectedAsAisle && emptySelectedAsAisle()}
                color="text-amber-700"
              >
                Seçili alanı boş bırak ({selectedSeats.length})
              </MenuItem>
              <MenuItem onClick={() => emptySelectedArea && emptySelectedArea()} danger>
                Seçili alanı boş kıl ({selectedSeats.length})
              </MenuItem>
            </>
          )}
          <ColumnRowActions />
          <Divider />
          <Label>Tip</Label>
          {seatTypes.map((t) => (
            <MenuItem
              key={t.value}
              onClick={() => {
                if (changeSelectedSeatType && pos) {
                  const key = `${Math.round(pos.row)}-${Math.round(pos.col)}`;
                  changeSelectedSeatType(t.value, [key]);
                }
              }}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
              {t.label}
            </MenuItem>
          ))}
        </>
      )}

      {/* —— Empty cell / canvas —— */}
      {(contextMenu.type === 'emptyArea' ||
        contextMenu.type === 'zone-edit-emptyArea' ||
        contextMenu.type === 'emptyAreaSelection' ||
        contextMenu.type === 'zone-edit-emptyAreaSelection') && (
        <>
          <Label>Bu nokta</Label>
          {selectedSeats.length > 0 && (
            <>
              <MenuItem
                onClick={() => emptySelectedAsAisle && emptySelectedAsAisle()}
                color="text-amber-700"
              >
                Seçili alanı boş bırak ({selectedSeats.length}) — numara devam eder
              </MenuItem>
              <MenuItem onClick={() => emptySelectedArea && emptySelectedArea()} danger>
                Seçili alanı boş kıl ({selectedSeats.length})
              </MenuItem>
              <Divider />
            </>
          )}
          {(contextMenu.type === 'emptyAreaSelection' ||
            contextMenu.type === 'zone-edit-emptyAreaSelection') &&
            contextMenu.selectionBox &&
            emptySelectedAsAisle && (
              <MenuItem
                onClick={() => {
                  const box = contextMenu.selectionBox;
                  const cellKeys = [];
                  for (let r = box.minRow; r <= box.maxRow; r += 1) {
                    for (let c = box.minCol; c <= box.maxCol; c += 1) {
                      cellKeys.push(`${r}-${c}`);
                    }
                  }
                  if (cellKeys.length) emptySelectedAsAisle(cellKeys);
                  setEmptyAreaSelection && setEmptyAreaSelection(null);
                }}
                color="text-amber-700"
              >
                Seçili alanı boş bırak (koridor)
              </MenuItem>
            )}
          {pos && addSeatAt && (
            <MenuItem
              onClick={() => {
                setActiveTool && setActiveTool('add');
                setAddSeatMode && setAddSeatMode(true);
                addSeatAt(pos.row, pos.col, selectedSeatType);
              }}
              color="text-[#AE256D]"
            >
              Buraya koltuk ekle
            </MenuItem>
          )}
          {pos && addRowFromPosition && (
            <MenuItem onClick={() => addRowFromPosition(pos.row, pos.col)} color="text-blue-700">
              Buradan sıra ekle (10)
            </MenuItem>
          )}
          {(contextMenu.type === 'emptyAreaSelection' ||
            contextMenu.type === 'zone-edit-emptyAreaSelection' ||
            contextMenu.selectionBox) &&
            fillEmptyAreaWithSeats && (
              <MenuItem
                onClick={() => {
                  fillEmptyAreaWithSeats(contextMenu.selectionBox || contextMenu.emptyBox);
                  setEmptyAreaSelection && setEmptyAreaSelection(null);
                }}
                color="text-green-700"
              >
                Seçili alanı doldur
              </MenuItem>
            )}
          <ColumnRowActions />
          <Divider />
          <Label>Koltuk tipi (sonraki)</Label>
          {seatTypes.map((t) => (
            <MenuItem
              key={t.value}
              onClick={() => setSelectedSeatType && setSelectedSeatType(t.value)}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
              {t.label}
              {selectedSeatType === t.value ? ' ✓' : ''}
            </MenuItem>
          ))}
        </>
      )}

      {/* —— Axis header (row letter / col number) —— */}
      {(contextMenu.type === 'col-header' || contextMenu.type === 'row-header') && (
        <>
          {contextMenu.type === 'col-header' && pos && (
            <>
              <Label>Kolon {pos.col + 1}</Label>
              <MenuItem onClick={() => emptyColumn && emptyColumn(pos.col)} color="text-amber-700">
                Kolonu boşalt (koridor) — numara devam eder
              </MenuItem>
              {colIsAisle || colIsVoid ? (
                <MenuItem
                  onClick={() => restoreColumn && restoreColumn(pos.col)}
                  color="text-green-700"
                >
                  Kolonu geri aç
                </MenuItem>
              ) : (
                <MenuItem onClick={() => voidColumn && voidColumn(pos.col)} color="text-orange-700">
                  Kolonu geçersiz kıl
                </MenuItem>
              )}
            </>
          )}
          {contextMenu.type === 'row-header' && pos && (
            <>
              <Label>Satır {pos.row + 1}</Label>
              <MenuItem onClick={() => voidRow && voidRow(pos.row)} color="text-orange-700">
                {rowIsVoid ? 'Satırı geri aç' : 'Satırı geçersiz kıl — harf devam eder'}
              </MenuItem>
            </>
          )}
        </>
      )}

      {/* —— Zone —— */}
      {(contextMenu.type === 'zone' ||
        contextMenu.type === 'zone-overview' ||
        contextMenu.type === 'zone-overview-zone') &&
        zone && (
          <>
            <Label>{zone.name}</Label>
            <MenuItem onClick={() => enterZone && enterZone(zone)} color="text-blue-700">
              Zone’a gir / seç
            </MenuItem>
            <MenuItem onClick={() => copyZone && copyZone(zone)} color="text-gray-800">
              Kopyala (Ctrl+C)
            </MenuItem>
            <MenuItem onClick={() => duplicateZone && duplicateZone(zone)} color="text-indigo-700">
              Çoğalt (Ctrl+D)
            </MenuItem>
            {pasteZone && (
              <MenuItem onClick={pasteZone} color="text-green-700">
                Yapıştır (Ctrl+V)
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                enterZone && enterZone(zone);
                setActiveTool && setActiveTool('add');
                setAddSeatMode && setAddSeatMode(true);
              }}
              color="text-[#AE256D]"
            >
              İçine koltuk ekle
            </MenuItem>
            {fillZoneWithSeats && (
              <MenuItem
                onClick={() =>
                  fillZoneWithSeats(zone, {
                    seatType: selectedSeatType,
                    rowGap: 1,
                    colGap: 1,
                  })
                }
                color="text-indigo-700"
              >
                Şekli koltukla doldur
              </MenuItem>
            )}
            {fillZoneTribune && (
              <MenuItem
                onClick={() =>
                  fillZoneTribune(zone, {
                    rows: 12,
                    seatsPerRow: 30,
                    seatType: selectedSeatType,
                  })
                }
                color="text-blue-600"
              >
                Tribün bloğu yerleştir
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={() => deleteZone && deleteZone(zone.id)} danger>
              Zone’u sil
            </MenuItem>
          </>
        )}

      {/* —— Zone overview empty —— */}
      {contextMenu.type === 'zone-overview-empty' && (
        <>
          <Label>Canvas</Label>
          {pasteZone && (
            <MenuItem onClick={pasteZone} color="text-green-700">
              Zone yapıştır (Ctrl+V)
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setActiveTool && setActiveTool('zone-create');
            }}
            color="text-[#AE256D]"
          >
            Burada zone çizmeye başla
          </MenuItem>
          <p className="px-3 py-2 text-[11px] text-gray-500 leading-snug">
            Sol menüden şekil seç (kutu / daire / serbest), sonra canvas’ta çiz.
          </p>
        </>
      )}
    </div>
  );
}
